// Amos-83 Request Portal — the ONLY surface a requester sees. Rendered by App.tsx for
// /portal/* before AuthenticatedApp/Layout ever mount (no nav, no staff routes).
import React, { useCallback, useEffect, useState } from 'react';
import { getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { COPY } from './copy';
import { portalApi, PortalApiError, Me } from './api';
import { NewPasswordScreen, ForgotPasswordScreen, ChangePasswordScreen, normalizeUsername } from './AuthScreens';
import RequestForm from './RequestForm';
import StatusTable from './StatusTable';

/** Amos-104 R5: on every portal screen. Mailto link only; no Terms link until a Terms document exists. */
export function PortalFooter() {
  const [before, after] = COPY.footer.split(COPY.footerEmail);
  return (
    <footer className="max-w-5xl mx-auto px-4 py-6 text-xs text-slate-500 text-center" data-testid="portal-footer">
      {before}<a href={`mailto:${COPY.footerEmail}`} className="underline hover:text-slate-700">{COPY.footerEmail}</a>{after}
    </footer>
  );
}

/** Amos-104 H3: the tab title while the portal is mounted; the staff app's title comes back on unmount. */
export function usePortalTitle() {
  useEffect(() => {
    const prev = document.title;
    document.title = COPY.tabTitle;
    return () => { document.title = prev; };
  }, []);
}

function PortalLogin({ onLogin }: { onLogin: () => void }) {
  const [view, setView] = useState<'signin' | 'newpw' | 'forgot'>('signin');
  const [info, setInfo] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const r = await signIn({ username: normalizeUsername(email), password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });
      if (r.isSignedIn) onLogin();
      else if (r.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') { setPassword(''); setView('newpw'); }
      else setError(COPY.signInFailed);
    } catch (err) {
      console.error('portal sign-in error', err);
      setError(COPY.signInFailed);
    } finally {
      setBusy(false);
    }
  };

  if (view !== 'signin') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
        {view === 'newpw'
          ? <NewPasswordScreen onDone={onLogin} onExpired={() => { setView('signin'); setError(COPY.signInNewExpired); }} />
          : <ForgotPasswordScreen initialEmail={email} onBack={() => setView('signin')} onDone={() => { setView('signin'); setInfo(COPY.signInResetDone); }} />}
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md" data-testid="portal-login">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">{COPY.appTitle}</h1>
          <p className="text-sm text-slate-500 mt-1">{COPY.appSubtitle}</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="p-email" className="block text-sm font-medium text-slate-700 mb-1">{COPY.signInEmail}</label>
            <input id="p-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label htmlFor="p-password" className="block text-sm font-medium text-slate-700 mb-1">{COPY.signInPassword}</label>
            <input id="p-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {info && <div role="status" className="text-green-800 text-sm bg-green-50 border border-green-200 rounded-md p-3" data-testid="portal-info">{info}</div>}
          {error && <div role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
          <button type="submit" disabled={busy}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors">
            {busy ? COPY.signInBusy : COPY.signInButton}
          </button>
        </form>
        <p className="mt-4 text-center"><button type="button" onClick={() => setView('forgot')} className="text-sm text-blue-700 underline hover:text-blue-900">{COPY.signInForgot}</button></p>
        <p className="text-xs text-slate-500 mt-6 text-center">{COPY.signInInvite}</p>
        <p className="text-xs text-slate-500 mt-3 text-center" data-testid="portal-signin-notice">{COPY.signInNotice}</p>
      </div>
      <PortalFooter />
    </div>
  );
}

export default function PortalApp() {
  const [checking, setChecking] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [meError, setMeError] = useState<'not_enabled' | 'expired' | 'other' | null>(null);
  const [tab, setTab] = useState<'new' | 'mine'>('new');
  const [refreshKey, setRefreshKey] = useState(0);
  const [changingPw, setChangingPw] = useState(false);
  const [pwChanged, setPwChanged] = useState(false);
  usePortalTitle();

  const loadMe = useCallback(async () => {
    setMeError(null);
    try {
      setMe(await portalApi.me());
    } catch (e) {
      const err = e as PortalApiError;
      if (err.status === 403) setMeError('not_enabled');
      else if (err.status === 401) setMeError('expired');
      else setMeError('other');
    }
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then(() => { setSignedIn(true); })
      .catch(() => { setSignedIn(false); })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (signedIn) loadMe(); }, [signedIn, loadMe]);

  const logout = async () => {
    try { await signOut(); } catch (e) { console.error(e); }
    setSignedIn(false); setMe(null); setMeError(null); setChangingPw(false); setPwChanged(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 text-sm">
        <div>{COPY.loading}</div>
        <PortalFooter />
      </div>
    );
  }
  if (!signedIn) return <PortalLogin onLogin={() => setSignedIn(true)} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-slate-800">{COPY.appTitle}</h1>
            <p className="text-xs text-slate-500">{COPY.appSubtitle}</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {me && (
              <>
                <span className="text-slate-600 hidden sm:inline" data-testid="portal-me-email">{me.email}</span>
                {/* Amos-104 R3: a label, not a control — plain text, no pill */}
                <span className="text-xs text-slate-500" data-testid="portal-broker-label">{COPY.brokerLabel(me.broker_group)}</span>
              </>
            )}
            {me && !changingPw && (
              <button onClick={() => { setPwChanged(false); setChangingPw(true); }} data-testid="portal-change-pw-link" className="text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline">{COPY.accountChangePassword}</button>
            )}
            <button onClick={logout} className="text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline">{COPY.signOut}</button>
          </div>
        </div>
        {me?.test_mode && (
          <div className="bg-amber-50 border-t border-amber-200 text-amber-800 text-xs text-center py-1" data-testid="portal-test-banner">{COPY.testBanner}</div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {meError === 'not_enabled' && (
          <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-4 text-sm" data-testid="portal-not-enabled">{COPY.notEnabled}</div>
        )}
        {meError === 'expired' && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm">
            {COPY.sessionExpired}{' '}
            <button onClick={logout} className="underline">{COPY.signInButton}</button>
          </div>
        )}
        {meError === 'other' && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm">
            {COPY.errNetwork}{' '}
            <button onClick={loadMe} className="underline">{COPY.statusRefresh}</button>
          </div>
        )}
        {pwChanged && (
          <div role="status" className="mb-4 text-green-800 text-sm bg-green-50 border border-green-200 rounded-md p-3" data-testid="portal-pw-changed">{COPY.accountChangeDone}</div>
        )}
        {me && changingPw && (
          <ChangePasswordScreen onDone={() => { setChangingPw(false); setPwChanged(true); }} onCancel={() => setChangingPw(false)} onSessionExpired={logout} />
        )}
        {me && !changingPw && (
          <>
            <nav className="border-b border-slate-200 mb-6 flex gap-4" aria-label="Portal sections">
              {([['new', COPY.tabNew], ['mine', COPY.tabMine]] as const).map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`portal-tab-${k}`}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                  {label}
                </button>
              ))}
            </nav>
            {tab === 'new'
              ? <RequestForm me={me} onSubmitted={() => setRefreshKey((k) => k + 1)} onViewMine={() => setTab('mine')} onSessionExpired={logout} />
              : <StatusTable key={refreshKey} onSessionExpired={logout} />}
          </>
        )}
      </main>
      <PortalFooter />
    </div>
  );
}
